import { Reference } from '@approvalmax/types';
import { compareHelpers, mathService, numberHelpers } from '@approvalmax/utils';
import uniqueId from 'lodash/uniqueId';
import { GetNetSuiteItemPriceLevels } from 'shared/data/webApp/v2';

import { isUnitPriceFieldDisabledByPriceLevel } from '../../components/NetSuiteInvoiceItemList/NetSuiteInvoiceItemList.helpers';
import { NetSuitePriceLevel, NetSuiteSalesPrice } from '../../data/types';
import { NetSuiteInvoiceListItemExpanded } from '../../data/types/NetSuiteInvoiceItems';
import {
    checkInvoiceListItemIsEmpty,
    isCustomPriceLevel,
    isListPriceLevel,
    setCurrencyPrecision,
    updateLineOnAmountChange,
    updateLineOnGrossAmountChange,
    updateLineOnQuantityOrUnitPriceChange,
    updateLineOnTaxCodeChange,
} from '../../helpers';

export const createDefaultInvoiceLine = (lineNumber: number) => {
    return {
        lineNumber,
        item: null,
        units: null,
        priceLevel: null,
        quantity: null,
        description: null,
        unitPrice: null,
        amount: null,
        taxCode: null,
        taxRate: null,
        taxAmount: null,
        grossAmount: null,
        class: null,
        department: null,
        location: null,
        customFields: [],
        isEmpty: false,
        checked: false,
        defaultRate: null,
        unitList: [],
        salesPrices: [],
        priceLevelList: [],
        id: uniqueId('netSuiteInvoiceListItem-'),
    };
};

export const updateInvoiceLineFieldValue = <K extends keyof NetSuiteInvoiceListItemExpanded>(
    line: NetSuiteInvoiceListItemExpanded,
    key: K,
    value: NetSuiteInvoiceListItemExpanded[K],
    currencyPrecision: number
): NetSuiteInvoiceListItemExpanded => {
    let newLine = {
        ...line,
        [key]: value,
    };

    const isUnitPriceDisabled = isUnitPriceFieldDisabledByPriceLevel(line);

    if (key === 'taxCode') {
        newLine = updateLineOnTaxCodeChange(line, newLine.taxCode, currencyPrecision);
    }

    if (key === 'amount') {
        newLine = updateLineOnAmountChange(line, newLine.amount, currencyPrecision, isUnitPriceDisabled);
    }

    if (key === 'grossAmount') {
        newLine = updateLineOnGrossAmountChange(line, newLine.grossAmount, currencyPrecision, isUnitPriceDisabled);
    }

    if (key === 'quantity' || key === 'unitPrice') {
        newLine = updateLineOnQuantityOrUnitPriceChange(line, newLine.quantity, newLine.unitPrice, currencyPrecision);
    }

    newLine.isEmpty = checkInvoiceListItemIsEmpty(newLine);

    return newLine;
};

export const mapPriceLevels = (priceLevels: GetNetSuiteItemPriceLevels['response'] | undefined) =>
    (priceLevels || [])
        .reduce<Reference[]>((result, priceLevel) => {
            if (priceLevel.id && priceLevel.name) {
                result.push({
                    id: priceLevel.id,
                    text: priceLevel.name,
                });
            }

            return result;
        }, [])
        .sort(compareHelpers.comparatorFor<Reference>(compareHelpers.stringComparator2Asc, 'id'));

export const mapPriceLevelList = (priceLevels: Reference[], salesPrices: NetSuiteSalesPrice | undefined) => {
    return priceLevels.reduce<NetSuitePriceLevel[]>((list, priceLevel) => {
        let price = salesPrices?.priceLevels.find(({ id }) => id === priceLevel.id)?.price ?? null;

        if (isCustomPriceLevel(priceLevel)) {
            const listPriceLevel = salesPrices?.priceLevels.find(isListPriceLevel);

            price = listPriceLevel?.price ?? null;
        }

        list.push({
            ...priceLevel,
            price,
        });

        return list;
    }, []);
};

export const calculateLineAmounts = (
    priceLevel: NetSuitePriceLevel | null,
    quantity: number | null,
    taxRate: number | null,
    currencyPrecision: number
) => {
    const defaultRate = priceLevel ? setCurrencyPrecision(priceLevel.price, currencyPrecision) : null;
    const unitPrice = isCustomPriceLevel(priceLevel) ? null : defaultRate;

    const amount =
        numberHelpers.isNumber(quantity) && numberHelpers.isNumber(unitPrice)
            ? setCurrencyPrecision(mathService.multiply(quantity, unitPrice), currencyPrecision)
            : null;

    const taxAmount = setCurrencyPrecision(mathService.multiply(amount || 0, (taxRate || 0) / 100), currencyPrecision);

    const grossAmount = numberHelpers.isNumber(amount)
        ? setCurrencyPrecision(mathService.add(amount, taxAmount), currencyPrecision)
        : null;

    return {
        amount,
        taxAmount,
        grossAmount,
        unitPrice,
        defaultRate,
    };
};

export const getBasePriceLevel = (priceLevelList: NetSuitePriceLevel[]) =>
    priceLevelList.find(isListPriceLevel) ?? priceLevelList[0] ?? null;
